import { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl } from 'framework/core';
import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import { PlatformModule } from '../platform/platform.module';
import { CartAPI } from './cart.api';
import { CartController } from './cart.controller';
import { CartSelectors } from './cart.selectors';
import { CartService } from './cart.service';

/**
 * Cart module managing cart functionality via Widget Integration framework
 *
 * This module orchestrates cart operations including:
 * - Cart modal display and interactions
 * - Add to cart functionality
 * - Cart state management
 * - Theme-specific cart icon handling
 *
 * Technical Support teams can extend this module for shop-specific customization:
 * - Override block listener setup via {@link setupBlockListener}
 * - Customize block initialization via {@link onInitBlock}
 * - Add custom error handling via {@link handleBlockInitError}
 *
 * @example
 * ```javascript
 * // Extend CartModule for custom initialization
 * window.boostWidgetIntegration.extend('CartModule', (CartModule) => {
 *   return class CustomCartModule extends CartModule {
 *     setupBlockListener() {
 *       super.setupBlockListener();
 *       // Add custom cart event handling
 *       this.registerCustomEventHandlers();
 *     }
 *
 *     async onInitBlock(block) {
 *       // Add shop-specific tracking before initialization
 *       this.trackCartBlockInit(block.id);
 *       await super.onInitBlock(block);
 *     }
 *
 *     registerCustomEventHandlers() {
 *       window.addEventListener('custom-cart-event', () => {
 *         this.controller?.openCart();
 *       });
 *     }
 *
 *     trackCartBlockInit(blockId) {
 *       window.dataLayer?.push({
 *         event: 'cart_block_initialized',
 *         blockId
 *       });
 *     }
 *   };
 * });
 * ```
 */
@Injectable
@Module({
  imports: [PlatformModule],
  providers: [CartService, CartAPI, CartSelectors, CartController],
  exports: [CartAPI, CartSelectors, CartController],
})
export class CartModule {
  controller: CartController | null = null;

  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService
  ) {
    this.setupBlockListener();
  }

  /**
   * Sets up the TAE App block listener for cart blocks.
   * Override this method to customize block registration logic or support additional block types.
   *
   * @example
   * ```javascript
   * // Add support for custom cart block types
   * protected setupBlockListener() {
   *   this.appService.TAEApp.addRegistryBlockListener((block) => {
   *     if (block.blockType === 'cart' || block.blockType === 'mini-cart') {
   *       this.onInitBlock(block);
   *     }
   *   });
   * }
   * ```
   */
  protected setupBlockListener(): void {
    this.appService.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'cart') {
        this.onInitBlock(block);
      }
    });
  }

  /**
   * Called when a cart block is initialized.
   * Override this method to customize cart initialization or add shop-specific setup logic.
   *
   * @param block - The AppBlock instance being initialized
   *
   * @example
   * ```javascript
   * // Add shop-specific initialization
   * protected async onInitBlock(block) {
   *   // Add custom tracking before cart initialization
   *   this.trackCartInit(block.id);
   *
   *   // Initialize cart with custom configuration
   *   await super.onInitBlock(block);
   *
   *   // Add post-initialization custom logic
   *   this.setupCustomCartFeatures(block);
   * }
   * ```
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      if (block.document instanceof HTMLElement) {
        const cartController: CartController = await this.moduleRef.resolve(CartController);
        this.controller = cartController;
        cartController.connect();
      }
    } catch (error) {
      this.handleBlockInitError(error as Error, block);
    }
  }

  /**
   * Handles errors that occur during cart block initialization.
   * Override this method to customize error handling, add custom logging, or provide user feedback.
   *
   * @param error - The error that occurred
   * @param block - The AppBlock that failed to initialize
   *
   * @example
   * ```javascript
   * // Add custom error handling and user notification
   * protected handleBlockInitError(error, block) {
   *   // Log to custom analytics service
   *   this.analyticsService.trackError('cart_init_failed', {
   *     blockId: block.id,
   *     errorMessage: error.message,
   *     errorStack: error.stack,
   *   });
   *
   *   // Call parent error handler
   *   super.handleBlockInitError(error, block);
   *
   *   // Show user-friendly error message
   *   this.showCartErrorNotification('Unable to load cart. Please refresh the page.');
   * }
   * ```
   */
  protected handleBlockInitError(error: Error, block: AppBlock): void {
    this.appService.logger.error(`Failed to initialize cart block ${block.id}:`, error);
  }
}
